/**
 * Phone numbers are stored as E.164 ("+919876543210"). Customers type them
 * many ways ("98765 43210", "09876543210", "+91 98765-43210"), so everything
 * is normalised before use. Indian mobiles may be typed without a country
 * code; any other country must be entered with its "+" prefix.
 */
export const DEFAULT_COUNTRY_CODE = '91';

const INDIAN_MOBILE = /^[6-9]\d{9}$/;

/** Returns the E.164 form, or null when the input is not a plausible mobile number. */
export function normalizePhone(input: string, defaultCountryCode: string = DEFAULT_COUNTRY_CODE): string | null {
  let s = input.trim().replace(/[\s\-().]/g, '');
  if (!s) return null;
  if (s.startsWith('00')) s = `+${s.slice(2)}`;

  if (s.startsWith('+')) {
    const digits = s.slice(1);
    if (!/^[1-9]\d{7,14}$/.test(digits)) return null;
    if (digits.startsWith('91') && !INDIAN_MOBILE.test(digits.slice(2))) return null; // +91 must be a real mobile
    return `+${digits}`;
  }

  if (!/^\d+$/.test(s)) return null;
  if (defaultCountryCode === '91') {
    if (INDIAN_MOBILE.test(s)) return `+91${s}`;
    if (s.length === 11 && s.startsWith('0') && INDIAN_MOBILE.test(s.slice(1))) return `+91${s.slice(1)}`;
    if (s.length === 12 && s.startsWith('91') && INDIAN_MOBILE.test(s.slice(2))) return `+${s}`;
    return null;
  }
  return null;
}

/** "+919876543210" -> "+91 98765 43210"; other countries are returned as stored. */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return '';
  const m = /^\+91([6-9]\d{4})(\d{5})$/.exec(e164);
  return m ? `+91 ${m[1]} ${m[2]}` : e164;
}

/** MSG91 wants the number with the country code and no "+". */
export const toMsg91Number = (e164: string) => e164.replace(/^\+/, '');
